import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { ArrowLeft, Trash2, AlertTriangle, Shield, Mail } from 'lucide-preact';
import { clearAllData } from '../lib/consent';
import { getSavedStories } from '../lib/storage';

export function Settings(_props: RoutableProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const storiesCount = getSavedStories().length;

  const handleClearAll = () => {
    clearAllData();
    route('/');
    window.location.reload();
  };

  return (
    <div className="min-h-screen gradient-warm">
      {/* Decorative background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl float" />
        <div
          className="absolute bottom-40 right-10 w-80 h-80 bg-moss/5 rounded-full blur-3xl float"
          style={{ animationDelay: '2s' }}
        />
      </div>

      <div className="relative container-custom py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <header className="flex items-center justify-between mb-10 animate-in">
            <h1 className="text-3xl sm:text-4xl font-display font-medium text-ink">Settings</h1>
            <button
              onClick={() => route('/')}
              className="flex items-center gap-2 px-4 py-2 text-ink/50 hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </button>
          </header>

          <div className="space-y-6">
            {/* Data Summary */}
            <div
              className="bg-cream rounded-3xl border-ornate p-8 animate-in"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-parchment border-ornate flex items-center justify-center">
                  <Shield size={20} className="text-rust" />
                </div>
                <h2 className="text-xl font-display font-medium text-ink">Your Data</h2>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gold/10">
                  <span className="text-ink/60">Saved stories</span>
                  <span className="font-medium text-ink">{storiesCount}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-ink/60">Data storage</span>
                  <span className="font-medium text-ink">This device only</span>
                </div>
              </div>
            </div>

            {/* Privacy Controls */}
            <div
              className="bg-cream rounded-3xl border-ornate p-8 animate-in"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-parchment border-ornate flex items-center justify-center">
                  <Trash2 size={20} className="text-rust" />
                </div>
                <h2 className="text-xl font-display font-medium text-ink">Privacy Controls</h2>
              </div>

              {!showConfirm ? (
                <div className="space-y-4">
                  <p className="text-ink/60 leading-relaxed">
                    You can delete all stories and revoke parental consent at any time. This action
                    cannot be undone.
                  </p>
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="px-6 py-3 bg-parchment text-ink rounded-xl font-medium
                             border-ornate hover:shadow-story transition-all cursor-pointer"
                  >
                    Delete All Data & Revoke Consent
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-5 bg-rust/5 border border-rust/20 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-rust flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-rust font-medium mb-2">Are you sure?</p>
                        <p className="text-rust/80 text-sm">
                          This will permanently delete all {storiesCount} saved stories and reset
                          parental consent. You will need to complete the parent verification again
                          to use the app.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="px-6 py-3 bg-parchment text-ink rounded-xl font-medium
                               border-ornate hover:shadow-story transition-all cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="px-6 py-3 bg-rust text-cream rounded-xl font-medium
                               hover:shadow-story transition-all cursor-pointer"
                    >
                      Yes, Delete Everything
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Links */}
            <div
              className="bg-cream rounded-3xl border-ornate p-8 animate-in"
              style={{ animationDelay: '0.3s' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-parchment border-ornate flex items-center justify-center">
                  <Mail size={20} className="text-rust" />
                </div>
                <h2 className="text-xl font-display font-medium text-ink">Legal & Contact</h2>
              </div>
              <div className="space-y-3">
                <a
                  href="/privacy"
                  className="block text-rust hover:text-rust/70 transition-colors cursor-pointer"
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  className="block text-rust hover:text-rust/70 transition-colors cursor-pointer"
                >
                  Terms of Service
                </a>
                <a
                  href="mailto:privacy@straitstreet.co"
                  className="block text-rust hover:text-rust/70 transition-colors cursor-pointer"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
